import { ConfirmationModalFooter } from '@/components/ConfirmationModal/Base/Footer';
import { useTranslation } from '@/i18n';
import {
  ButtonV2Props,
  ModalWithoutPortal,
} from '@moneyforward/ap-frontend-components';
import { FC, memo, useCallback, useMemo } from 'react';
import { useBulkDeleteAdminInvoiceReport } from './hooks/useBulkDeleteAdminInvoiceReport';

export type ConfirmBulkDeleteModalProps = {
  bulkDeleteModalOpen: boolean;
  bulkDeleteCount: number;
  bulkDeleteIds: string[];
  onCloseBulkDeleteModal: () => void;
  refetch: () => void | Promise<unknown>;
  selectedId?: string | null;
  onDrawerClose?: () => void;
};

export const ConfirmBulkDeleteModal: FC<ConfirmBulkDeleteModalProps> = memo(
  ({
    bulkDeleteModalOpen,
    bulkDeleteCount,
    bulkDeleteIds,
    onCloseBulkDeleteModal,
    refetch,
    selectedId,
    onDrawerClose,
  }) => {
    const { t } = useTranslation();

    const { bulkDeleteReports } = useBulkDeleteAdminInvoiceReport({
      refetch,
      onCloseBulkDeleteModal,
      selectedId,
      onDrawerClose,
    });

    const handleDelete = useCallback(() => {
      return bulkDeleteReports(bulkDeleteIds)();
    }, [bulkDeleteReports, bulkDeleteIds]);

    const modalTitle = useMemo(() => {
      return t('admin_invoice_reports.bulk_delete_confirm_title', {
        count: bulkDeleteCount,
      });
    }, [bulkDeleteCount, t]);

    const modalContent = useMemo(() => {
      return t('admin_invoice_reports.bulk_delete_confirm_message', {
        count: bulkDeleteCount,
      });
    }, [bulkDeleteCount, t]);

    const cancelButtonProps: ButtonV2Props = useMemo(
      () => ({
        isTertiary: true,
        onClick: onCloseBulkDeleteModal,
        size: 'sm',
        children: t('cancel'),
      }),
      [onCloseBulkDeleteModal, t]
    );

    const deleteButtonProps: ButtonV2Props = useMemo(
      () => ({
        color: 'danger',
        onClick: handleDelete,
        size: 'sm',
        children: t('delete'),
      }),
      [handleDelete, t]
    );

    return (
      <ModalWithoutPortal
        open={bulkDeleteModalOpen}
        onClose={onCloseBulkDeleteModal}
        size='sm'
        title={modalTitle}
        mask
        footer={
          <ConfirmationModalFooter
            cancelButton={cancelButtonProps}
            okButton={deleteButtonProps}
          />
        }
      >
        <div>{modalContent}</div>
      </ModalWithoutPortal>
    );
  }
);

ConfirmBulkDeleteModal.displayName = 'AdminConfirmBulkDeleteModal';
